/**
 * Planner data handed to route screens through React context instead of direct store
 * subscriptions, so screens that `KeepAliveRoutes` keeps hidden stay current without delaying
 * their reveal.
 *
 * Why: a hidden `<Activity>` disconnects its store subscriptions. When the activity list changed
 * while a screen was hidden (every calendar refresh replaces it), revealing that screen forced a
 * synchronous full re-render before the frame that shows it: 100–200 ms for the board, table and
 * backlog. A context value, by contrast, reaches a hidden screen as an idle-priority re-render in
 * the background, so on reveal the screen is already up to date and only becomes visible.
 *
 * `RouteDataProvider` subscribes once, above the retained screens. Store actions are stable and
 * are still read from the stores directly.
 *
 * @module apps/web/src/stores/routeData
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useScheduleStore, type ScheduleState } from '@/stores/scheduleStore';
import { useTasksStore } from '@/stores/tasksStore';
import type { TaskUI } from '@fluxability/types';

/** The calendar placements screens render. */
export interface RouteSchedule {
  /** Committed schedule blocks. */
  blocks: ScheduleState['blocks'];
  /** External calendar commitments. */
  calendarEvents: ScheduleState['calendarEvents'];
  /** Suggested placements. */
  ghostBlocks: ScheduleState['ghostBlocks'];
}

/** Current activities, or null outside a provider. */
const TasksContext = createContext<TaskUI[] | null>(null);

/** Current placements, or null outside a provider. */
const ScheduleContext = createContext<RouteSchedule | null>(null);

/** Inputs of `RouteDataProvider`. */
interface RouteDataProviderProps {
  /** The route screens. */
  children: ReactNode;
}

/** Subscribe to activities and placements once and provide them to every route screen. */
export function RouteDataProvider({ children }: RouteDataProviderProps) {
  const tasks = useTasksStore(s => s.tasks);
  const blocks = useScheduleStore(s => s.blocks);
  const calendarEvents = useScheduleStore(s => s.calendarEvents);
  const ghostBlocks = useScheduleStore(s => s.ghostBlocks);
  const schedule = useMemo(
    () => ({ blocks, calendarEvents, ghostBlocks }),
    [blocks, calendarEvents, ghostBlocks]
  );
  return (
    <TasksContext.Provider value={tasks}>
      <ScheduleContext.Provider value={schedule}>{children}</ScheduleContext.Provider>
    </TasksContext.Provider>
  );
}

/**
 * Current activities for a route screen. Inside `RouteDataProvider` this is the context value;
 * outside one (isolated harnesses) it is the store's current list, without a subscription.
 */
export function useRouteTasks(): TaskUI[] {
  return useContext(TasksContext) ?? useTasksStore.getState().tasks;
}

/** Current placements for a route screen; same provider rules as `useRouteTasks`. */
export function useRouteSchedule(): RouteSchedule {
  const schedule = useContext(ScheduleContext);
  if (schedule) return schedule;
  const { blocks, calendarEvents, ghostBlocks } = useScheduleStore.getState();
  return { blocks, calendarEvents, ghostBlocks };
}
